import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronDown, Hand, MessagesSquare, Play, Shuffle } from 'lucide-react';
import Button from '../ui/Button';
import { STAT_CARD_BASE } from '../ui/StatCard';
import ManualPairingDialog from '../microsim/ManualPairingDialog';
import microSimService from '../../services/microSimService';
import { showError } from '../../utils/toast';

/**
 * "Run a micro simulation" — the entry point from a live session.
 *
 * Self-contained, like LaunchSimulationPanel, so the 3,000-line SessionControlPage only
 * gains one line. Renders nothing for a trainer without access (the catalogue call is
 * refused) or when no topic is published. A launch already running in this session is
 * offered as "Open" instead of starting a second one — the server also refuses that.
 *
 * Pairs are random by default; "Choose pairs" lets the trainer make them by hand
 * (ManualPairingDialog), with the room listed by participant number.
 */
const PAIRING_MODES = [
  { key: 'random', label: 'Random', icon: Shuffle },
  { key: 'manual', label: 'Choose pairs', icon: Hand },
];

const LaunchMicroSimPanel = ({ sessionId }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [detail, setDetail] = useState(null);
  const [industry, setIndustry] = useState('');
  const [starting, setStarting] = useState(false);
  const [mode, setMode] = useState('random');
  const [people, setPeople] = useState(null); // null until "Choose pairs" loads the room
  const [loadingPeople, setLoadingPeople] = useState(false);

  useEffect(() => {
    let alive = true;
    microSimService.list()
      .then((res) => {
        if (!alive) return;
        const list = res.microSims || [];
        setItems(list);
        if (list[0]) setSlug(list[0].slug);
      })
      .catch(() => { /* no access or none published: render nothing */ });
    microSimService.activeLaunch(sessionId)
      .then((res) => {
        if (alive && res?.launch) { setActive(res.launch); setOpen(true); }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [sessionId]);

  // Industry versions belong to the chosen topic.
  useEffect(() => {
    if (!slug) return undefined;
    let alive = true;
    microSimService.get(slug)
      .then((res) => {
        if (!alive) return;
        setDetail(res.microSim);
        setIndustry(res.microSim.industries[0]?.key || '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  if (!items.length && !active) return null;

  const loadPeople = async () => {
    const res = await microSimService.candidates(sessionId);
    setPeople(res.participants || []);
  };

  // pairing: undefined for random pairs, or the manual request from the dialog.
  const start = async (pairing) => {
    setStarting(true);
    try {
      const res = await microSimService.launch({ slug, sessionId, industry, pairing });
      navigate(`/trainer/micro-sim-launches/${res.launch.id}`);
    } catch (err) {
      if (err?.data?.launch?.id) {
        navigate(`/trainer/micro-sim-launches/${err.data.launch.id}`);
        return;
      }
      showError(err.userMessage || err.message || 'Could not start the micro simulation');
      setStarting(false);
      // Someone may have left or joined: show the room as it is now.
      if (pairing) loadPeople().catch(() => {});
    }
  };

  const choosePairs = async () => {
    setLoadingPeople(true);
    try {
      await loadPeople();
    } catch (err) {
      showError(err.userMessage || 'Could not load the participants');
    } finally {
      setLoadingPeople(false);
    }
  };

  return (
    <div className={`${STAT_CARD_BASE} overflow-hidden`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400">
          <MessagesSquare size={17} strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-neutral-900 dark:text-neutral-100">Run a micro simulation</span>
          <span className="block text-xs text-neutral-500 dark:text-neutral-400">Pair everyone up for a practice conversation</span>
        </span>
        <ChevronDown size={16} className={`shrink-0 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="space-y-3 border-t border-neutral-200 px-5 py-4 dark:border-neutral-700/60">
          {active ? (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50 px-3.5 py-3 dark:border-violet-500/30 dark:bg-violet-500/10">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{active.title}</p>
                <p className="text-xs text-neutral-600 dark:text-neutral-300">Running now</p>
              </div>
              <Button variant="primary-filled" size="sm" onClick={() => navigate(`/trainer/micro-sim-launches/${active.id}`)}>
                Open <ArrowRight size={14} className="ml-1.5" />
              </Button>
            </div>
          ) : (
            <>
              <label className="block text-sm">
                <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Topic</span>
                <select
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-neutral-200 bg-white py-2 pl-3 pr-10 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                >
                  {items.map((i) => <option key={i.slug} value={i.slug}>{i.title} · {i.theme}</option>)}
                </select>
              </label>
              {detail?.industries?.length > 1 && (
                <label className="block text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Industry version</span>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-neutral-200 bg-white py-2 pl-3 pr-10 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                  >
                    {detail.industries.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
                  </select>
                </label>
              )}
              <div className="text-sm">
                <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Pairs</span>
                <div className="mt-1 grid grid-cols-2 gap-1 rounded-xl bg-neutral-100 p-1 dark:bg-neutral-800" role="radiogroup" aria-label="How to pair">
                  {PAIRING_MODES.map(({ key, label, icon: Icon }) => (
                    <button
                      key={key}
                      type="button"
                      role="radio"
                      aria-checked={mode === key}
                      onClick={() => setMode(key)}
                      className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold ${
                        mode === key
                          ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-900 dark:text-neutral-100'
                          : 'text-neutral-500 hover:text-neutral-800 dark:text-neutral-400'
                      }`}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>
              </div>
              {mode === 'random' ? (
                <>
                  <Button variant="primary-filled" fullWidth onClick={() => start()} loading={starting} disabled={!slug}>
                    <Play size={14} className="mr-1.5" /> Pair everyone and start
                  </Button>
                  <p className="text-[11px] text-neutral-400">
                    Everyone who has joined is paired at random. With an odd number, one person observes a pair.
                  </p>
                </>
              ) : (
                <>
                  <Button variant="primary-filled" fullWidth onClick={choosePairs} loading={loadingPeople} disabled={!slug}>
                    <Hand size={14} className="mr-1.5" /> Choose pairs
                  </Button>
                  <p className="text-[11px] text-neutral-400">
                    Pick who plays with whom, by participant number. Anyone you leave out can be paired at random.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}

      <ManualPairingDialog
        isOpen={people !== null}
        onClose={() => setPeople(null)}
        people={people || []}
        onSubmit={(spec) => start({ mode: 'manual', ...spec })}
        submitting={starting}
      />
    </div>
  );
};

LaunchMicroSimPanel.propTypes = { sessionId: PropTypes.string.isRequired };

export default LaunchMicroSimPanel;
